import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { Product } from '../../../services/api';
import { useOrgFeatureFlag } from '../../../hooks/useOrgFeatureFlags';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { tokens } from '../../../theme/tokens';
import { lifecycleStageOf } from '../../../utils/productBrowserFilterState';
import {
  CameraWithPricing,
  ProductPriceHistoryRow,
  formatDate,
  formatRelativeDate,
  formatStockStatus,
  historyPrimaryLine,
  lastPriceChangeAt,
  laterDate,
  linkedVariantKindLabel,
  mergePriceHistoryWithRejectedReview,
  toAbsoluteImageUrl,
  useProductPriceFormatters,
  variantOptionIsCurrent,
} from '../../../utils/productDetailHelpers';
import {
  applyCatalogBarcodesToVariants,
  completeFamilyVariantRows,
  variantDisplayLabel,
  type NormalizedProductVariant,
} from '../../../utils/productVariants';
import { catalogRegularFromPricing, getEffectiveMapForProduct, rebateWindowCaption } from '../../../utils/mapPricing';
import { manufacturerSiteUrl } from '../../../utils/manufacturerSiteUrl';
import { countryOfOriginFromProduct, japanArticleNumber } from '../../../utils/productIdentityExtras';
import { PendingReviewChip } from '../../PendingReviewChip';
import { StatusChip } from '../../ui';
import VariantGroupSelectAll from '../../productBrowser/VariantGroupSelectAll';
import {
  BUNDLE_TONE,
  Dot,
  ExternalGlyph,
  InlineSectionHeader,
  LockGlyph,
  OutlineButton,
  Pill,
  SourceBadge,
  SourceKind,
  TextLink,
  Thumb,
  Tone,
  WarnBanner,
} from './primitives';
import { buildClassification, dealerMargin, historyDelta, ListingModel, livePromo, usd } from './dealerModel';

export interface DealerOverviewTabProps {
  product: CameraWithPricing;
  history: ProductPriceHistoryRow[];
  displayVariants: NormalizedProductVariant[];
  linkedVariants: Product[];
  linkedVariantsLoading: boolean;
  variantSwitchLoadingSku: string | null;
  onVariantClick: (variant: NormalizedProductVariant) => void;
  onLinkedProductClick: (product: Product) => void;
  onOpenGallery: (images: string[], title: string, index: number) => void;
  /** Live price of the store's mapped listing, when read. */
  storefrontPrice: number | null;
  listing: ListingModel;
  overrideCount: number;
  inSync: boolean;
  syncSelectedIds: Set<string>;
  selectionIntegrationId?: string | null;
  compatCounts: Array<{ label: string; count: number }> | null;
  onOpenStore: () => void;
  onOpenPricing: () => void;
  onOpenCompatibility: () => void;
  onReportClassification: (issue: string) => void;
  classificationReported: boolean;
  classificationReportPending: boolean;
  onReviewDiscount: (decision: 'approved' | 'rejected') => void;
  reviewPending: boolean;
  /** False for a platform admin outside impersonation: there is no store listing to show. */
  hasStore?: boolean;
  /** Effective role: false while an admin is impersonating, so the store's view is shown. */
  isPlatformAdmin?: boolean;
}

interface Cell {
  label: string;
  value: string;
  note: string;
  noteTone?: 'caution';
  lock?: boolean;
  amber?: boolean;
  tooltip?: string;
}

interface Fact {
  label: string;
  value?: string | null;
  source?: SourceKind;
  hint?: string | null;
  pill?: { label: string; tone: Tone };
  estimate?: { label: string; tooltip: string };
}

const LISTING_DOT = {
  in_sync: tokens.signal.positive.solid,
  price_differs: tokens.signal.caution.solid,
  pending: tokens.ink.disabled,
  not_in_store: tokens.ink.disabled,
} as const;

const KIND_TONE: Record<string, { bg: string; ink: string }> = {
  kit: { bg: tokens.signal.info.tint, ink: tokens.signal.info.ink },
  bundle: BUNDLE_TONE,
  configuration: { bg: tokens.surface.tint, ink: tokens.ink.bodyAlt },
  style: { bg: tokens.surface.tint, ink: tokens.ink.bodyAlt },
};

/** Fields `enrichment.filledFrom` credits to Barcode Lookup. */
function barcodeLookupFields(product: Product): Set<string> {
  const fields = new Set<string>();
  for (const fill of product.enrichment?.filledFrom || []) {
    if (!/barcodelookup/i.test(String(fill.source || ''))) continue;
    for (const field of fill.fields || []) fields.add(field);
  }
  return fields;
}

function isUpcPairing(upc: string | undefined, ean: string): boolean {
  const u = String(upc || '').replace(/\D/g, '');
  const e = ean.replace(/\D/g, '');
  return u.length === 12 && (e === `0${u}` || e === u);
}

function mpnSource(product: Product, lookupFields: Set<string>): SourceKind | undefined {
  if (!product.mpn) return 'missing';
  if (lookupFields.has('mpn')) return 'lookup';
  if (product.sku && product.mpn === product.sku) return 'derived';
  return undefined;
}

const regionalDate = (r?: { date?: string; precision?: string } | null): { value: string; hint: string | null } | null => {
  if (!r?.date) return null;
  const d = new Date(r.date);
  if (!Number.isFinite(d.getTime())) return { value: r.date, hint: null };
  if (r.precision === 'month') return { value: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }), hint: 'Month only' };
  if (r.precision === 'quarter') return { value: `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`, hint: 'Quarter only' };
  if (r.precision === 'year') return { value: String(d.getUTCFullYear()), hint: 'Year only' };
  return { value: formatDate(r.date) || r.date, hint: null };
};

export function DealerOverviewTab(props: DealerOverviewTabProps) {
  const { product, listing } = props;
  const hasStore = props.hasStore !== false;
  const isPlatformAdmin = props.isPlatformAdmin === true;
  const navigate = useNavigate();
  // Same gate as ProductActionsRow: the real admin role AND the effective one.
  const { isAdmin } = useAuth();
  const canOpenProductManager = isPlatformAdmin && isAdmin;
  const { t } = useAppTranslation();
  const { enabled: shippingEstimateOn } = useOrgFeatureFlag('shipping_weight_fallback');
  const { saleDisplay, rebateDisplay, dealerReimbursement, dealerCostDetail, formatMoneyForProduct } = useProductPriceFormatters();
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Price ──────────────────────────────────────────────────────────────────
  const sale = saleDisplay(product);
  const rebate = rebateDisplay(product);
  const em = getEffectiveMapForProduct(product);
  const rebateActive = em?.windowStatus === 'active' && rebate.instantRebate > 0;
  const rebateUpcoming = em?.windowStatus === 'upcoming' && rebate.instantRebate > 0;
  const money = (n: number | null | undefined) => formatMoneyForProduct(product, n) ?? '—';
  const currency = String(product.currency || '').toUpperCase() === 'CAD' ? 'CAD' : 'USD';
  const liveStorefront = props.storefrontPrice != null && props.storefrontPrice > 0 ? props.storefrontPrice : null;
  const leadAmount = liveStorefront ?? (sale.sellingPrice > 0 ? sale.sellingPrice : product.price > 0 ? product.price : null);
  // The live listing is the lead when read; it reads as the rebate price when it equals MAP after rebate.
  const liveIsRebatePrice = liveStorefront != null && rebateActive && em?.afterIR != null && Math.abs(liveStorefront - em.afterIR) < 0.01;
  const showsAfterRebate = rebateActive && (liveStorefront == null || liveIsRebatePrice);
  const struck = liveIsRebatePrice && em?.raw
    ? money(em.raw)
    : liveStorefront == null && sale.isSaleActive && sale.regularPrice > 0 ? money(sale.regularPrice) : null;
  const msrp = Number(product.pricing?.msrp ?? 0);
  const msrpDiffers = msrp > 0 && Math.abs(msrp - catalogRegularFromPricing(product.pricing)) >= 0.01;

  const captionParts: string[] = [];
  if (liveStorefront != null) captionParts.push('Live on your store.');
  if (msrp > 0 && (msrpDiffers || !em?.raw)) captionParts.push(`MSRP ${money(msrp)}.`);
  if ((rebateActive || rebateUpcoming) && em) {
    const window = rebateWindowCaption(em, formatDate);
    captionParts.push(`The ${money(rebate.instantRebate)} instant rebate ${rebateUpcoming ? 'starts' : 'runs'}${window ? ` ${window}` : ''}.`);
  } else if (!rebateActive && sale.isSaleActive && sale.savingsAmount > 0) {
    captionParts.push(`${money(sale.savingsAmount)} sale discount.`);
  }
  if (!em?.raw && msrp <= 0 && !liveStorefront) captionParts.push('No MAP on this product, so you can price freely.');

  const privateLocked = !isPlatformAdmin && (product.vendorPrivateLocked === true || product.pricingData?.vendorPrivateLocked === true);
  const costDetail = !privateLocked ? dealerCostDetail(product) : null;
  const cost = costDetail?.amount ?? 0;
  const credit = dealerReimbursement(product);
  const margin = dealerMargin(leadAmount, cost > 0 ? cost : null, rebateActive ? credit : 0);

  const cells: Cell[] = [];
  if (em?.raw) {
    cells.push(
      rebateActive && em.afterIR != null
        ? { label: 'MAP after rebate', value: money(em.afterIR), note: `${money(em.raw)} − ${money(rebate.instantRebate)} IR` }
        : { label: 'MAP', value: money(em.raw), note: em.mapAsOf ? `as of ${formatDate(em.mapAsOf)}` : '' },
    );
  } else {
    if (msrp > 0) cells.push({ label: 'MSRP', value: money(msrp), note: '' });
    cells.push({ label: 'MAP', value: 'None', note: 'Price freely' });
  }
  if (cost > 0) {
    const promasterDate = costDetail?.source === 'promaster' ? formatDate(costDetail.asOf) : null;
    cells.push({
      label: 'Dealer cost',
      value: money(cost),
      lock: true,
      note: margin
        ? `${money(margin.amount)} margin (${margin.pct.toFixed(1)}%)${rebateActive && credit > 0 ? ' with reimbursement' : ''}`
        : promasterDate
          ? t('store.promasterDealerCost.costCaption', { date: promasterDate })
          : 'Your cost · never advertised',
      noteTone: costDetail?.stale ? 'caution' : undefined,
      tooltip: costDetail?.source === 'promaster'
        ? costDetail.stale
          ? t('store.promasterDealerCost.staleTooltip', { date: promasterDate })
          : t('store.promasterDealerCost.tooltip')
        : undefined,
    });
  }
  if (credit > 0) cells.push({ label: 'Dealer reimbursement', value: money(credit), note: 'Internal — never advertised', lock: true, amber: true });

  const promo = livePromo(product);
  const historyRows = mergePriceHistoryWithRejectedReview(props.history, product.pricing?.suspiciousDiscountReview)
    .filter((row) => historyPrimaryLine(row, (n) => formatMoneyForProduct(product, n)) !== 'Price recorded');
  const shownHistory = historyOpen ? historyRows : historyRows.slice(0, 3);
  const lastChange = lastPriceChangeAt(product);
  const lastChecked = laterDate(product.pricing?.priceLastChecked, product.pricing?.priceAsOf, product.lastScrapedAt);
  const review = product.pricing?.suspiciousDiscountReview;
  const missingPricePending = product.pricing?.missingAdvertisedPriceReview?.status === 'pending';

  // ── Gallery ────────────────────────────────────────────────────────────────
  const images = product.images ?? [];
  const openGallery = (index: number) => {
    const urls = images.map((img) => toAbsoluteImageUrl(img)).filter((u): u is string => !!u);
    props.onOpenGallery(urls, product.name || product.model, index);
  };

  // ── Variants ───────────────────────────────────────────────────────────────
  const familyRows = useMemo(() => {
    const family = completeFamilyVariantRows(props.displayVariants, product, props.linkedVariants);
    const resolved = applyCatalogBarcodesToVariants(family, [product, ...props.linkedVariants]);
    const mpnCounts: Record<string, number> = {};
    resolved.forEach((v) => { if (v.mpn) mpnCounts[v.mpn] = (mpnCounts[v.mpn] || 0) + 1; });
    const bySku = new Map(props.linkedVariants.filter((p) => p.sku).map((p) => [String(p.sku).trim(), p]));
    const sharedMpns = Object.keys(mpnCounts).filter((k) => mpnCounts[k] > 1);
    const missingUpc = resolved.filter((v) => !v.upc && !v.ean).length;
    const issues: string[] = [];
    if (sharedMpns.length) issues.push(sharedMpns.length === 1 ? `MPN ${sharedMpns[0]} is repeated across ${mpnCounts[sharedMpns[0]]} options` : `${sharedMpns.length} MPNs are repeated across options`);
    if (missingUpc) issues.push(`${missingUpc} option${missingUpc > 1 ? 's have' : ' has'} no UPC or EAN`);
    return {
      rows: resolved.map((v) => {
        const sku = (v.sku || '').trim();
        const linked = sku ? bySku.get(sku) : undefined;
        const isCurrent = variantOptionIsCurrent(product, v);
        const productId = isCurrent ? product.id : linked?.id;
        return {
          v,
          label: variantDisplayLabel(v),
          isCurrent,
          img: toAbsoluteImageUrl((isCurrent ? product.images?.[0] : linked?.images?.[0]) ?? undefined) || null,
          listed: hasStore && productId ? (isCurrent ? props.inSync : props.syncSelectedIds.has(productId)) : null,
          sharedMpn: !!v.mpn && mpnCounts[v.mpn] > 1,
          canOpen: !!sku && !isCurrent,
          loading: !!sku && props.variantSwitchLoadingSku === sku,
        };
      }),
      warning: issues.length ? `${issues.join(' · ')}. A shared MPN is how a body's pricing row gets stamped onto a kit.` : null,
    };
  }, [props.displayVariants, props.linkedVariants, product, props.inSync, props.syncSelectedIds, props.variantSwitchLoadingSku, hasStore]);
  const familySkus = new Set(familyRows.rows.map((r) => (r.v.sku || '').trim()).filter(Boolean));
  const extraLinked = props.linkedVariants.filter((p) => !familySkus.has(String(p.sku || '').trim()));

  // ── Facts ──────────────────────────────────────────────────────────────────
  const lookupFields = barcodeLookupFields(product);
  const identity: Fact[] = [];
  identity.push({ label: 'Brand', value: product.brand });
  identity.push({ label: 'Model', value: product.model || null, source: product.model ? undefined : 'missing' });
  identity.push({ label: 'SKU', value: product.sku || null, source: product.sku ? undefined : 'missing' });
  identity.push({ label: 'MPN', value: product.mpn || null, source: mpnSource(product, lookupFields), hint: product.mpn && product.mpn === product.sku && !lookupFields.has('mpn') ? 'Same as SKU' : null });
  identity.push({ label: 'UPC', value: product.upc || null, source: product.upc ? undefined : 'missing' });
  const eanValue = product.ean && product.ean !== product.upc ? product.ean : null;
  const eanFromUpc = eanValue != null && isUpcPairing(product.upc, eanValue);
  identity.push({
    label: 'EAN',
    value: eanValue,
    source: eanValue ? (eanFromUpc ? 'derived' : lookupFields.has('ean') ? 'lookup' : undefined) : 'missing',
    hint: eanFromUpc ? 'UPC padded to 13 digits' : null,
  });
  if (product.gtin) {
    const padded = product.upc ? product.upc.padStart(14, '0') : null;
    identity.push({ label: 'GTIN', value: product.gtin, source: padded === product.gtin ? 'derived' : undefined, hint: padded === product.gtin ? 'UPC padded to 14 digits' : null });
  }
  const jan = japanArticleNumber(product);
  if (jan && jan !== product.upc && jan !== product.ean) identity.push({ label: 'JAN', value: jan });
  const origin = countryOfOriginFromProduct(product);
  if (origin) identity.push({ label: 'Country of origin', value: origin });
  if (product.dimensions?.weight != null) identity.push({ label: 'Weight', value: `${product.dimensions.weight} ${product.dimensions.weightUnit || 'g'}` });
  const dims = [product.dimensions?.length, product.dimensions?.width, product.dimensions?.height].filter((v): v is number => v != null && Number.isFinite(Number(v)));
  if (dims.length) identity.push({ label: 'Dimensions', value: `${dims.join(' × ')} ${product.dimensions?.unit || 'mm'}` });
  const est = product.estimatedShippingDimensions;
  if (shippingEstimateOn && est && Number(est.weight) > 0 && est.wouldPush !== false && !(Number(product.dimensions?.weight) > 0) && !(Number(product.shippingDimensions?.weight) > 0)) {
    const estimate = { label: t('products.shippingEstimate.estimated'), tooltip: est.source === 'gemini' ? t('products.shippingEstimate.sourceGemini') : t('products.shippingEstimate.sourceCategory') };
    identity.push({ label: t('products.shippingEstimate.weight'), value: `${est.weight} ${est.weightUnit || 'g'}`, estimate });
    const ed = [est.length, est.width, est.height].filter((v): v is number => v != null && Number.isFinite(Number(v)));
    if (ed.length === 3) identity.push({ label: t('products.shippingEstimate.dimensions'), value: `${ed.join(' × ')} ${est.unit || 'mm'}`, estimate });
  }
  if (product.vendorName || product.source) identity.push({ label: 'Source', value: product.vendorName || product.source || null });

  const lifecycle: Fact[] = [];
  const status = product.isArchived ? 'archived' : product.status;
  if (status) {
    lifecycle.push({
      label: 'Status',
      pill: { label: status.charAt(0).toUpperCase() + status.slice(1), tone: status === 'active' ? 'positive' : status === 'discontinued' ? 'caution' : 'neutral' },
    });
  }
  // Pre-release stage (flag `preorder_lifecycle`): the status stays Active while a product is announced or in pre-order.
  const stage = lifecycleStageOf(product);
  if (stage !== 'released') {
    const ships = product.lifecycle?.releaseDate ? formatDate(product.lifecycle.releaseDate) : null;
    lifecycle.push({
      label: 'Stage',
      pill: { label: t(`productLifecycle.stage.${stage}`), tone: stage === 'preorder' ? 'caution' : 'info' },
      hint: t('productLifecycle.ships', { date: ships || t('productLifecycle.dateTbd') }),
    });
  }
  const ms = product.manufacturerStock;
  if (ms?.status && ms.status !== 'unknown') {
    const checked = formatRelativeDate(ms.checkedAt) || formatDate(ms.checkedAt);
    lifecycle.push({
      label: `Stock at ${product.brand}`,
      pill: {
        label: ms.label || formatStockStatus(ms.status),
        tone: ms.status === 'in_stock' ? 'positive' : ms.status === 'out_of_stock' || ms.status === 'discontinued' ? 'problem' : 'caution',
      },
      source: 'scrape',
      hint: checked ? `Checked ${checked} · not pushed` : 'Not pushed',
    });
  }
  const ld = product.launchDates as
    | { announced?: string; us?: { date?: string; precision?: string }; canada?: { date?: string; precision?: string }; global?: { date?: string; precision?: string }; source?: string; confidence?: number }
    | undefined;
  const ldSource: SourceKind | undefined = ld?.source === 'gemini_grounded' ? 'ai' : ld?.source === 'scraped' ? 'scrape' : ld?.source === 'manual' ? 'manual' : undefined;
  if (ld?.announced) lifecycle.push({ label: 'Announced', value: formatDate(ld.announced) || ld.announced, source: ldSource });
  const us = regionalDate(ld?.us ?? ld?.global);
  lifecycle.push(us ? { label: 'US release', value: us.value, hint: us.hint, source: ldSource } : { label: 'US release', value: null, source: 'missing' });
  const ca = regionalDate(ld?.canada);
  lifecycle.push(ca ? { label: 'Canada release', value: ca.value, hint: ca.hint, source: ldSource } : { label: 'Canada release', value: null, source: 'missing' });
  if (product.launchDate && !ld?.us && !ld?.global) lifecycle.push({ label: 'Launched', value: formatDate(product.launchDate) || product.launchDate });
  if (product.firstImportedAt) lifecycle.push({ label: 'First imported', value: formatDate(product.firstImportedAt), hint: formatRelativeDate(product.firstImportedAt) });
  if (product.lastScrapedAt) lifecycle.push({ label: 'Listing updated', value: formatDate(product.lastScrapedAt), hint: formatRelativeDate(product.lastScrapedAt) });
  if (product.archivedAt) lifecycle.push({ label: 'Archived', value: formatDate(product.archivedAt), hint: formatRelativeDate(product.archivedAt) });
  const lifecycleNote = ld?.source === 'gemini_grounded'
    ? `Release dates via Gemini${typeof ld.confidence === 'number' ? ` · ${Math.round(ld.confidence * (ld.confidence <= 1 ? 100 : 1))}% confident` : ''}`
    : '';

  const classification = buildClassification(product);
  const manufacturerUrl = manufacturerSiteUrl(product);
  const compatTotal = props.compatCounts?.reduce((n, g) => n + g.count, 0) ?? 0;

  const storeLine =
    listing.state === 'not_in_store'
      ? 'Not listed yet'
      : listing.state === 'price_differs'
        ? `Listing shows ${usd(liveStorefront)}, catalog differs`
        : listing.state === 'pending'
          ? 'No live listing read yet'
          : listing.pushes[0]
            ? `Last push ${listing.pushes[0].when}`
            : 'Live';
  const overridesLabel = props.overrideCount > 0 ? `${props.overrideCount} override${props.overrideCount === 1 ? '' : 's'}` : 'No overrides';

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
      {/* Left: photos and tags */}
      <Box sx={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px', p: { xs: '16px', sm: '22px' }, boxSizing: 'border-box' }}>
        <ButtonBase
          onClick={() => images.length && openGallery(0)}
          disabled={!images.length}
          aria-label={images.length ? `Open photo gallery for ${product.model || 'this product'}` : undefined}
          sx={{ position: 'relative', height: 300, borderRadius: '12px', bgcolor: tokens.surface.sunken, border: '1px solid', borderColor: tokens.line.hairline, overflow: 'hidden', p: 1.5 }}
        >
          {images[0] ? (
            <Box component="img" src={toAbsoluteImageUrl(images[0])} alt={product.model} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <Typography sx={{ fontSize: '12.5px', color: tokens.ink.faint }}>No product photo</Typography>
          )}
        </ButtonBase>
        {images.length > 1 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px' }}>
            {images.slice(0, 6).map((img, i) => (
              <ButtonBase
                key={i}
                onClick={() => openGallery(i)}
                aria-label={`Open photo ${i + 1} of ${images.length}`}
                sx={{ position: 'relative', height: 50, borderRadius: '8px', bgcolor: tokens.surface.sunken, border: '1px solid', borderColor: i === 0 ? tokens.accent.main : tokens.line.hairline, overflow: 'hidden' }}
              >
                <Box component="img" src={toAbsoluteImageUrl(img)} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                {i === 5 && images.length > 6 && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(15,23,42,0.64)', color: tokens.ink.inverse, fontSize: '12px', fontWeight: 600 }}>
                    +{images.length - 5}
                  </Box>
                )}
              </ButtonBase>
            ))}
          </Box>
        )}
        {images.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', color: tokens.ink.faint }}>{images.length} photo{images.length === 1 ? '' : 's'}</Typography>
            {images.length > 1 && <TextLink onClick={() => openGallery(0)}>View all photos</TextLink>}
          </Box>
        )}
        {(product.tags?.length ?? 0) > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', mt: '8px', pt: '16px', borderTop: '1px solid', borderColor: tokens.line.hairline }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>Tags</Typography>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {product.tags!.map((tag) => (
                <Box key={tag} component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 23, px: '8px', borderRadius: '7px', bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt, fontSize: '12px' }}>{tag}</Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Right */}
      <Box sx={{ flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '24px', p: { xs: '16px', sm: '22px' }, boxShadow: `inset 1px 0 0 ${tokens.line.hairline}` }}>
        {/* Price block */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ fontSize: '28px', fontWeight: 700, letterSpacing: '-0.02em', color: showsAfterRebate ? tokens.signal.positive.ink : tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {leadAmount != null ? money(leadAmount) : '—'}
                </Typography>
                <Typography component="span" sx={{ fontSize: '14px', color: tokens.ink.muted }}>{currency}</Typography>
                {struck && <Typography component="span" sx={{ fontSize: '14px', color: tokens.ink.faint, textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>{struck}</Typography>}
                {showsAfterRebate && <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: tokens.signal.positive.ink }}>after rebate</Typography>}
                <PendingReviewChip pricing={product.pricing} sx={{ alignSelf: 'center' }} />
              </Box>
              {captionParts.length > 0 && (
                <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{captionParts.join(' ')}</Typography>
              )}
            </Box>
            {cells.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'stretch', border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', overflow: 'hidden', flexWrap: 'wrap' }}>
                {cells.map((c, i) => {
                  const ink = c.amber ? tokens.signal.caution.ink : tokens.ink.faint;
                  const cell = (
                    <Box key={c.label} sx={{ display: 'flex', flexDirection: 'column', gap: '2px', px: '14px', py: '8px', bgcolor: c.amber ? tokens.signal.caution.row : tokens.surface.card, boxShadow: i ? `inset 1px 0 0 ${tokens.line.default}` : 'none' }}>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: ink }}>
                        {c.lock && <LockGlyph />}
                        <span>{c.label}</span>
                      </Box>
                      <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, color: tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>{c.value}</Typography>
                      <Typography component="span" sx={{ fontSize: '11px', color: c.noteTone === 'caution' ? tokens.signal.caution.ink : ink }}>{c.note || ' '}</Typography>
                    </Box>
                  );
                  return c.tooltip ? <Tooltip key={c.label} title={c.tooltip}>{cell}</Tooltip> : cell;
                })}
              </Box>
            )}
          </Box>

          {privateLocked && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '13px', py: '9px', borderRadius: '10px', border: '1px dashed', borderColor: tokens.line.default, bgcolor: tokens.surface.sunken, flexWrap: 'wrap' }}>
              <Box component="span" sx={{ color: tokens.ink.faint, display: 'inline-flex' }}><LockGlyph /></Box>
              <Typography sx={{ flex: '1 1 240px', fontSize: '12.5px', color: tokens.ink.bodyAlt }}>
                Dealer cost and reimbursement for {product.brand} unlock once your dealer authorization for this brand is verified.
              </Typography>
              <TextLink onClick={() => navigate('/app/store-settings')}>Verify brand</TextLink>
            </Box>
          )}

          {product.isProOnly && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '14px', py: '10px', border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', bgcolor: tokens.surface.sunken, flexWrap: 'wrap' }}>
              <Typography sx={{ flex: '1 1 240px', fontSize: '12.5px', color: tokens.ink.bodyAlt }}>
                Not on {product.brand}'s consumer site: sold only through the manufacturer's professional channel.
              </Typography>
              {product.proSiteUrl && <TextLink href={product.proSiteUrl}>Visit pro site</TextLink>}
            </Box>
          )}

          {missingPricePending && (
            <WarnBanner>
              <strong>No advertised price to sync.</strong>{' '}
              {isPlatformAdmin
                ? 'Motzu has no retail, MAP, or MSRP to send to the storefront, so sync skipped this SKU instead of pushing $0. Add manufacturer pricing to clear the flag.'
                : 'Motzu has no price to push for this SKU, so sync skipped it. Motzu admins have been flagged. Set a store price override in Your store if you still want it listed.'}
            </WarnBanner>
          )}

          {review && review.status !== 'rejected' && (
            <WarnBanner
              action={
                isPlatformAdmin && review.status === 'pending' ? (
                  <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <OutlineButton height={30} disabled={props.reviewPending} onClick={() => props.onReviewDiscount('approved')}>Approve sync</OutlineButton>
                    <OutlineButton height={30} disabled={props.reviewPending} onClick={() => props.onReviewDiscount('rejected')}>Reject</OutlineButton>
                  </Box>
                ) : undefined
              }
            >
              <strong>{review.status === 'approved' ? 'Approved for sync.' : 'Held for review.'}</strong>{' '}
              Advertised {money(Number(review.advertisedPrice))} is under 50% of stored {review.floorKind === 'map' ? 'MAP' : 'MSRP'} {money(Number(review.floor))}.
              {review.status === 'pending'
                ? isPlatformAdmin
                  ? ' Check the stored catalog and market pricing before approving a store sync.'
                  : ' Motzu admins will review the stored catalog pricing before this price is pushed.'
                : ' Future pricing syncs will push this price.'}
            </WarnBanner>
          )}

          {promo && (
            <ButtonBase
              onClick={props.onOpenPricing}
              sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left', justifyContent: 'flex-start', px: '14px', py: '11px', border: '1px solid', borderColor: tokens.accent.border, borderRadius: '10px', bgcolor: tokens.signal.info.row, flexWrap: 'wrap', '&:hover': { borderColor: tokens.accent.main } }}
            >
              <Dot color={tokens.signal.positive.solid} size={7} />
              <Box sx={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600, color: tokens.ink.primary }}>{promo.title}</Typography>
                <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.bodyAlt }}>{promo.body}</Typography>
              </Box>
              <Typography component="span" sx={{ flex: '0 0 auto', fontSize: '12.5px', fontWeight: 600, color: tokens.accent.main }}>See pricing calendar</Typography>
            </ButtonBase>
          )}

          {historyRows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '12px', borderTop: '1px solid', borderColor: tokens.line.hairline }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>Price change history</Typography>
                {(lastChange || lastChecked) && (
                  <Typography sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>
                    {[lastChange ? `Last change ${formatDate(lastChange)}` : null, lastChecked ? `checked ${formatDate(lastChecked)}` : null].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
              {shownHistory.map((row) => {
                const delta = historyDelta(row);
                return (
                  <Box key={row.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '96px minmax(0,1fr) auto', sm: '120px minmax(0,1fr) auto' }, gap: '12px', alignItems: 'baseline', py: '6px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, fontSize: '12.5px' }}>
                    <Typography component="span" sx={{ fontSize: 'inherit', color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>{formatDate(row.changedAt || row.newValue?.effectiveDate) || '—'}</Typography>
                    <Typography component="span" sx={{ fontSize: 'inherit', color: tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>{historyPrimaryLine(row, (n) => formatMoneyForProduct(product, n))}</Typography>
                    <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: delta?.direction === 'down' ? tokens.signal.positive.ink : delta ? tokens.signal.caution.ink : tokens.ink.muted }}>{delta?.label ?? ''}</Typography>
                  </Box>
                );
              })}
              {historyRows.length > 3 && (
                <TextLink onClick={() => setHistoryOpen((v) => !v)}>{historyOpen ? 'Show fewer' : `Show all ${historyRows.length} changes`}</TextLink>
              )}
            </Box>
          )}
        </Box>

        {/* Link buttons */}
        {(manufacturerUrl || props.compatCounts || canOpenProductManager) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {manufacturerUrl && <OutlineButton href={manufacturerUrl} endIcon={<ExternalGlyph />}>Manufacturer site</OutlineButton>}
            {canOpenProductManager && (
              <OutlineButton onClick={() => navigate(`/app/admin/products?productId=${encodeURIComponent(product.id)}`)}>Open in Product Manager</OutlineButton>
            )}
            {props.compatCounts && (
              <OutlineButton onClick={props.onOpenCompatibility}>
                <span>Works with</span>
                <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 500, color: tokens.ink.muted }}>
                  {compatTotal === 0 ? 'No links yet' : props.compatCounts.filter((g) => g.count > 0).map((g) => `${g.count} ${g.label.toLowerCase()}`).join(' · ')}
                </Typography>
              </OutlineButton>
            )}
          </Box>
        )}

        {/* Store status line */}
        {hasStore && (
        <ButtonBase
          onClick={props.onOpenStore}
          sx={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left', justifyContent: 'flex-start', px: '14px', py: '11px', border: '1px solid', borderColor: listing.state === 'price_differs' ? tokens.signal.caution.border : tokens.line.default, borderRadius: '10px', bgcolor: tokens.surface.card, flexWrap: 'wrap', '&:hover': { borderColor: tokens.ink.disabled } }}
        >
          <Dot color={LISTING_DOT[listing.state]} size={8} />
          <Box component="span" sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600, color: tokens.ink.primary }}>{listing.title}</Typography>
            <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{storeLine} · {overridesLabel}</Typography>
          </Box>
          <Typography component="span" sx={{ flex: '0 0 auto', fontSize: '12.5px', fontWeight: 600, color: tokens.accent.main }}>Manage in Your store</Typography>
        </ButtonBase>
        )}

        {/* Variants & kits */}
        {(familyRows.rows.length > 1 || extraLinked.length > 0 || props.linkedVariantsLoading) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <InlineSectionHeader
              title="Variants & kits"
              count={familyRows.rows.length > 1 ? familyRows.rows.length : undefined}
              note="Every configuration in this family"
            />
            {hasStore && product.variantGroupId && (
              <Box sx={{ '&:empty': { display: 'none' } }}>
                <VariantGroupSelectAll variant="button" productId={product.id} variantGroupId={product.variantGroupId} integrationId={props.selectionIntegrationId} selectedIds={props.syncSelectedIds} />
              </Box>
            )}
            {familyRows.rows.length > 1 && (
              <>
                <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '36px minmax(0,1fr) 84px 112px 86px' }, gap: '12px', pt: '2px', pb: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                  <span /><span>Option</span><span>SKU</span><span>UPC</span><span style={{ textAlign: 'right' }}>{hasStore ? 'In your store' : ''}</span>
                </Box>
                {familyRows.rows.map((r, i) => {
                  const tone = (r.v.kind && KIND_TONE[r.v.kind]) || KIND_TONE.configuration;
                  return (
                    <Box
                      key={`${r.v.sku || r.label}-${i}`}
                      role={r.canOpen ? 'button' : undefined}
                      tabIndex={r.canOpen ? 0 : undefined}
                      onClick={() => r.canOpen && !r.loading && props.onVariantClick(r.v)}
                      onKeyDown={(e) => { if (r.canOpen && !r.loading && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); props.onVariantClick(r.v); } }}
                      sx={{ display: 'grid', gridTemplateColumns: { xs: '36px minmax(0,1fr) 72px', sm: '36px minmax(0,1fr) 84px 112px 86px' }, gap: '12px', alignItems: 'center', py: '6px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, bgcolor: r.isCurrent ? tokens.surface.sunken : 'transparent', cursor: r.canOpen ? 'pointer' : 'default', '&:hover': r.canOpen ? { bgcolor: tokens.surface.sunken } : undefined }}
                    >
                      <Thumb src={r.img} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px 8px', minWidth: 0, flexWrap: 'wrap' }}>
                        {r.v.kind && (
                          <Box component="span" sx={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', height: 19, px: '7px', borderRadius: '5px', bgcolor: tone.bg, color: tone.ink, fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{r.v.kind}</Box>
                        )}
                        <Typography component="span" sx={{ minWidth: 0, fontSize: '13px', lineHeight: 1.4, fontWeight: r.isCurrent ? 600 : 400, color: tokens.ink.primary }}>{r.label}</Typography>
                        {r.isCurrent && <Typography component="span" sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>viewing</Typography>}
                        {r.loading && <CircularProgress size={12} />}
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', minWidth: 0 }}>
                        <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.bodyAlt, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.v.sku || '—'}</Typography>
                        {r.v.mpn && r.v.mpn !== r.v.sku && (
                          <Typography component="span" sx={{ fontSize: '11px', color: r.sharedMpn ? tokens.signal.caution.ink : tokens.ink.faint, overflow: 'hidden', textOverflow: 'ellipsis' }}>MPN {r.v.mpn}</Typography>
                        )}
                      </Box>
                      <Typography component="span" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '12.5px', color: r.v.upc || r.v.ean ? tokens.ink.muted : tokens.signal.caution.ink, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.v.upc || r.v.ean || 'Missing'}
                      </Typography>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', fontSize: '12.5px', color: r.listed ? tokens.signal.positive.ink : tokens.ink.muted }}>
                        {r.listed != null && <Dot color={r.listed ? tokens.signal.positive.solid : tokens.ink.disabled} />}
                        <span>{r.listed == null ? '—' : r.listed ? 'Listed' : 'Not added'}</span>
                      </Box>
                    </Box>
                  );
                })}
                {familyRows.warning && <WarnBanner>{familyRows.warning}</WarnBanner>}
              </>
            )}
            {(props.linkedVariantsLoading || extraLinked.length > 0) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mt: familyRows.rows.length > 1 ? '10px' : 0 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>Linked catalog products</Typography>
                {props.linkedVariantsLoading && extraLinked.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>Looking up linked configurations…</Typography>
                  </Box>
                ) : (
                  extraLinked.map((p) => {
                    const current = p.id === product.id;
                    const listed = current ? props.inSync : props.syncSelectedIds.has(p.id);
                    return (
                      <Box
                        key={p.id}
                        role={current ? undefined : 'button'}
                        tabIndex={current ? undefined : 0}
                        onClick={() => !current && props.onLinkedProductClick(p)}
                        onKeyDown={(e) => { if (!current && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); props.onLinkedProductClick(p); } }}
                        sx={{ display: 'grid', gridTemplateColumns: { xs: '36px minmax(0,1fr) 72px', sm: '36px minmax(0,1fr) 84px 112px 86px' }, gap: '12px', alignItems: 'center', py: '6px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, cursor: current ? 'default' : 'pointer', '&:hover': current ? undefined : { bgcolor: tokens.surface.sunken } }}
                      >
                        <Thumb src={toAbsoluteImageUrl(p.images?.[0]) || null} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px 8px', minWidth: 0, flexWrap: 'wrap' }}>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 19, px: '7px', borderRadius: '5px', bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt, fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{linkedVariantKindLabel(p)}</Box>
                          <Typography component="span" sx={{ minWidth: 0, fontSize: '13px', lineHeight: 1.4, color: tokens.ink.primary }}>{p.name || p.model}</Typography>
                          {current && <Typography component="span" sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>viewing</Typography>}
                        </Box>
                        <Typography component="span" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '12.5px', color: tokens.ink.bodyAlt, fontVariantNumeric: 'tabular-nums' }}>{p.sku || '—'}</Typography>
                        <Typography component="span" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '12.5px', color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>{p.upc || p.ean || '—'}</Typography>
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', fontSize: '12.5px', color: listed ? tokens.signal.positive.ink : tokens.ink.muted }}>
                          {hasStore && <Dot color={listed ? tokens.signal.positive.solid : tokens.ink.disabled} />}
                          <span>{!hasStore ? '—' : listed ? 'Listed' : 'Not added'}</span>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Identity and lifecycle */}
        {[
          { title: 'Identity', note: 'Badge shows where a value came from', rows: identity },
          { title: 'Availability & lifecycle', note: lifecycleNote, rows: lifecycle },
        ].map((block) => (
          <Box key={block.title} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <InlineSectionHeader title={block.title} note={block.note || undefined} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(190px, 1fr))' }, gap: '4px 24px' }}>
              {block.rows.map((f) => (
                <Box key={f.label} sx={{ display: 'flex', flexDirection: 'column', gap: '4px', py: '9px', borderBottom: '1px solid', borderColor: tokens.surface.sunken, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Typography component="span" sx={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.muted }}>{f.label}</Typography>
                    {f.source && <SourceBadge kind={f.source} />}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', minWidth: 0 }}>
                    {f.pill ? (
                      <Pill label={f.pill.label} tone={f.pill.tone} />
                    ) : (
                      <Typography component="span" sx={{ fontSize: '14px', lineHeight: 1.45, color: f.value ? tokens.ink.primary : tokens.ink.faint, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>{f.value || '—'}</Typography>
                    )}
                    {f.estimate && (
                      <Tooltip title={f.estimate.tooltip}>
                        <Box component="span" sx={{ display: 'inline-flex' }}><StatusChip label={f.estimate.label} tone="caution" /></Box>
                      </Tooltip>
                    )}
                  </Box>
                  {f.hint && <Typography component="span" sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>{f.hint}</Typography>}
                </Box>
              ))}
            </Box>
          </Box>
        ))}

        {/* Classification */}
        {classification && (
          <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: classification.disagreements ? tokens.signal.caution.border : tokens.line.default, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', px: '16px', py: '13px', borderBottom: '1px solid', borderColor: tokens.line.hairline, flexWrap: 'wrap' }}>
              <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600 }}>Classification</Typography>
              <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>Fields that should agree</Typography>
              <Box sx={{ flex: '1 1 auto' }} />
              {classification.disagreements
                ? <Pill tone="caution" label={`${classification.disagreements} disagree${classification.disagreements === 1 ? 's' : ''}`} />
                : <Pill tone="positive" label="All agree" />}
            </Box>
            {classification.rows.map((r) => (
              <Box key={r.key} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) auto', sm: 'minmax(120px,180px) minmax(0,1fr) auto' }, gap: '14px', alignItems: 'center', minHeight: 42, px: '16px', py: { xs: '8px', sm: 0 }, borderBottom: '1px solid', borderColor: tokens.surface.sunken, bgcolor: r.conflict ? tokens.signal.caution.row : 'transparent' }}>
                <Typography component="span" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '12.5px', color: tokens.ink.muted, fontFamily: tokens.font.mono }}>{r.key}</Typography>
                <Typography component="span" sx={{ fontSize: '14px', color: tokens.ink.primary, minWidth: 0, wordBreak: 'break-word' }}>{r.value}</Typography>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: r.conflict ? tokens.signal.caution.ink : tokens.signal.positive.ink, whiteSpace: 'nowrap' }}>
                  <Dot color={r.conflict ? tokens.signal.caution.solid : tokens.signal.positive.solid} />
                  {r.conflict ?? 'Agrees'}
                </Box>
              </Box>
            ))}
            {classification.issue && (
              <WarnBanner
                sx={{ m: '12px 16px', alignItems: 'center' }}
                action={
                  <ButtonBase
                    onClick={() => !props.classificationReported && !props.classificationReportPending && props.onReportClassification(classification.issue!)}
                    disabled={props.classificationReported || props.classificationReportPending}
                    sx={{ flex: '0 0 auto', height: 30, px: '12px', border: '1px solid', borderColor: tokens.signal.caution.border, borderRadius: '8px', bgcolor: tokens.surface.card, color: tokens.signal.caution.ink, fontSize: '12.5px', fontWeight: 600 }}
                  >
                    {props.classificationReported ? 'Reported' : props.classificationReportPending ? 'Reporting…' : 'Report to Motzu'}
                  </ButtonBase>
                }
              >
                {classification.issue}
              </WarnBanner>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
